import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SimpleMarkdownRenderer } from '@components/Markdown/SimpleMarkdownRenderer';
import type { RuleComplianceAssessment } from '@features/jotaudit/types';
import { useCreateTaskFromRuleMutation } from '@features/rule-build/store/api';
import { useCan } from '@features/user';
import {
  BarChart as AnalyticsIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  rulesApi,
  useDisableRuleMutation,
  useEnableRuleMutation,
  useGetRuleQuery,
} from '../store/api';
import type { ComplianceRuleDocument, LearningRuleDocument, ProviderRuleResponse } from '../types';

interface RuleDetailPanelProps {
  rule: ProviderRuleResponse | null;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Whether this is being used in admin context (allows editing global rules) */
  isAdmin?: boolean;
  /** Assessment data from audit (includes reason, suggestion, compliant status) */
  assessment?: RuleComplianceAssessment | null;
  /** Note ID if opened from note context (for analytics tracking) */
  noteId?: string;
}

export const RuleDetailPanel: React.FC<RuleDetailPanelProps> = ({
  rule: initialRule,
  onClose,
  onDelete,
  isAdmin = false,
  assessment,
  noteId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ADR 0073 Phase 3 (GDN-1590): parity with hasRole('practice_administrator').
  const isPracticeAdmin = useCan('manage_members');
  const [ruleDescriptionExpanded, setRuleDescriptionExpanded] = useState(false);
  const { noteAudit } = useEventTracking();

  // Fetch live rule data from cache to get updates after mutations
  const { data: liveRule } = useGetRuleQuery(initialRule?.rule_id || '', {
    skip: !initialRule?.rule_id,
  });

  // Use live rule data if available, otherwise fall back to initial prop
  const rule = liveRule || initialRule;

  const [disableRule, { isLoading: isDisabling }] = useDisableRuleMutation();
  const [enableRule, { isLoading: isEnabling }] = useEnableRuleMutation();
  const [createTaskFromRule, { isLoading: isCreatingTask }] = useCreateTaskFromRuleMutation();

  if (!rule) return null;

  const isDisabled = rule.is_disabled ?? false;
  const canDisable = (isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance' && !isDisabled;
  const canEnable = (isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance' && isDisabled;

  const handleDisable = async () => {
    if (!rule) return;

    try {
      await disableRule({
        ruleId: rule.rule_id,
        providerRuleDisableRequest: {
          scope: 'org',
        },
      }).unwrap();

      // Invalidate cache to refresh rule list, rule details, and search results
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: rule.rule_id },
          { type: 'RuleList', id: 'LIST' },
          { type: 'RuleSearch', id: 'LIST' },
        ]),
      );
      enqueueSnackbar('Rule disabled successfully', { variant: 'success' });
      
      // Track rule disabled (if from note context)
      if (noteId) {
        noteAudit.ruleDisabled({
          note_id: noteId,
          rule_id: rule.rule_id,
          source: 'audit_sidebar',
        });
      }
    } catch (error) {
      console.error('Failed to disable rule:', error);
      enqueueSnackbar('Failed to disable rule', { variant: 'error' });
    }
  };

  const handleEnable = async () => {
    if (!rule) return;

    try {
      await enableRule({
        ruleId: rule.rule_id,
        providerRuleDisableRequest: {
          scope: 'org',
        },
      }).unwrap();

      // Invalidate cache to refresh rule list, rule details, and search results
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: rule.rule_id },
          { type: 'RuleList', id: 'LIST' },
          { type: 'RuleSearch', id: 'LIST' },
        ]),
      );
      enqueueSnackbar('Rule enabled successfully', { variant: 'success' });
      
      // Track rule enabled (if from note context)
      if (noteId) {
        noteAudit.ruleEnabled({
          note_id: noteId,
          rule_id: rule.rule_id,
          source: 'audit_sidebar',
        });
      }
    } catch (error) {
      console.error('Failed to enable rule:', error);
      enqueueSnackbar('Failed to enable rule', { variant: 'error' });
    }
  };

  const handleEditRule = async () => {
    if (!rule) return;

    // Track edit clicked (if from note context)
    if (noteId) {
      noteAudit.ruleEditClicked({
        note_id: noteId,
        rule_id: rule.rule_id,
        source: 'audit_sidebar',
      });
    }

    try {
      // Create a task from the existing rule
      const result = await createTaskFromRule({ ruleId: rule.rule_id }).unwrap();
      
      // Close modal and navigate to rule-build page
      onClose();
      navigate(`/rule-build/${result.task_id}`);
    } catch (error) {
      console.error('Failed to create task from rule:', error);
      enqueueSnackbar('Failed to load rule editor. Please try again.', { variant: 'error' });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        borderRadius: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header with Status Badge and Title */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Top Row: Status Badge + Close */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {assessment && (
            <Chip
              label={`STATUS: ${assessment.compliant === 'no' ? 'NON-COMPLIANT' : assessment.compliant === 'warning' ? 'WARNING' : 'COMPLIANT'}`}
              sx={{
                bgcolor:
                  assessment.compliant === 'no'
                    ? '#FFD4CC'
                    : assessment.compliant === 'warning'
                      ? '#FFE8CC'
                      : '#CCE8CC',
                color:
                  assessment.compliant === 'no'
                    ? '#8B3226'
                    : assessment.compliant === 'warning'
                      ? '#8B6226'
                      : '#2E7D32',
                fontWeight: 600,
                letterSpacing: '0.5px',
                fontSize: '0.7rem',
                height: 28,
              }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small">
              <MoreVertIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Reason Box (if assessment exists) */}
        {assessment?.reason && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor:
                assessment.compliant === 'no'
                  ? 'error.light'
                  : assessment.compliant === 'warning'
                    ? 'warning.light'
                    : 'success.light',
              borderLeft: 4,
              borderColor:
                assessment.compliant === 'no'
                  ? 'error.main'
                  : assessment.compliant === 'warning'
                    ? 'warning.main'
                    : 'success.main',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              REASON
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {assessment.reason}
            </Typography>
          </Paper>
        )}

        {/* Rule Title and Source */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, color: 'text.primary' }}>
            {rule.rule_type === 'compliance'
              ? (rule.rule as ComplianceRuleDocument).title ||
                (rule.rule as ComplianceRuleDocument).selector_text ||
                'Untitled Rule'
              : (rule.rule as LearningRuleDocument).title || 'Untitled Rule'}
          </Typography>

          {/* Source Information */}
          {rule.rule_type === 'compliance' &&
            (rule.rule as ComplianceRuleDocument).source?.document_title && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {(rule.rule as ComplianceRuleDocument).source?.document_title}
                {(rule.rule as ComplianceRuleDocument).source?.document_date &&
                  ` • ${(rule.rule as ComplianceRuleDocument).source?.document_date}`}
              </Typography>
            )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
        {/* Rule Description Section */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box
            onClick={() => setRuleDescriptionExpanded(!ruleDescriptionExpanded)}
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body1" sx={{ flex: 1, fontWeight: 500 }}>
              Rule description
            </Typography>
            <ExpandMoreIcon
              sx={{
                transform: ruleDescriptionExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
          <Collapse in={ruleDescriptionExpanded}>
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              {rule.rule_type === 'compliance' ? (
                <>
                  <Box sx={{ '& p': { mb: 1 }, '& p:last-child': { mb: 0 } }}>
                    <SimpleMarkdownRenderer
                      markdown={
                        (rule.rule as ComplianceRuleDocument).body_md ||
                        'This rule checks that the CPT code billed accurately reflects the services documented in the note. Documentation should support the type, scope, and complexity of care provided.'
                      }
                    />
                  </Box>

                  {/* Facets/Context - if present */}
                  {((rule.rule as ComplianceRuleDocument).facets?.payer?.length ||
                    (rule.rule as ComplianceRuleDocument).facets?.cpt?.length ||
                    (rule.rule as ComplianceRuleDocument).facets?.provider_type?.length) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}
                      >
                        Applies to
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(rule.rule as ComplianceRuleDocument).facets?.payer?.map(payer => (
                          <Chip
                            key={payer}
                            label={payer}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {(rule.rule as ComplianceRuleDocument).facets?.cpt?.map(cpt => (
                          <Chip
                            key={cpt}
                            label={`CPT ${cpt}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {(rule.rule as ComplianceRuleDocument).facets?.provider_type?.map(type => (
                          <Chip
                            key={type}
                            label={type}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ '& p': { mb: 1 }, '& p:last-child': { mb: 0 } }}>
                  <SimpleMarkdownRenderer
                    markdown={String(
                      (rule.rule as LearningRuleDocument).body_md || 'Learning rule description',
                    )}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* Footer/Actions Bar */}
      <Box
        sx={{
          p: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Close Button */}
        <Button
          variant="contained"
          size="large"
          onClick={onClose}
          sx={{
            flex: 1,
            bgcolor: '#5E35B1',
            color: 'white',
            fontWeight: 600,
            textTransform: 'uppercase',
            py: 1.5,
            '&:hover': {
              bgcolor: '#4527A0',
            },
          }}
        >
          Close
        </Button>

        {/* Secondary Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Admin Actions Box */}
          {(onDelete ||
            isAdmin ||
            ((isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance') ||
            canEnable ||
            canDisable) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: 'text.secondary',
                  textAlign: 'center',
                  mb: 0.5,
                }}
              >
                You have access to these because you are an administrator
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {/* Settings/More Options */}
                {(onDelete || isAdmin) && (
                  <IconButton size="medium">
                    <SettingsIcon />
                  </IconButton>
                )}

                {/* Analytics Button - Navigate to analytics page if org admin */}
                {(isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance' && (
                  <IconButton
                    size="medium"
                    onClick={() => {
                      // Track analytics clicked (if from note context)
                      if (noteId) {
                        noteAudit.ruleAnalyticsClicked({
                          note_id: noteId,
                          rule_id: rule.rule_id,
                          source: 'audit_sidebar',
                        });
                      }
                      onClose();
                      navigate(`/jotaudit/rules/${rule.rule_id}/analytics`);
                    }}
                  >
                    <AnalyticsIcon />
                  </IconButton>
                )}

                {/* Edit Button - Navigate to rule-build tool if org admin */}
                {(isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance' && (
                  <IconButton
                    size="medium"
                    onClick={handleEditRule}
                    disabled={isCreatingTask}
                  >
                    <EditIcon />
                  </IconButton>
                )}

                {/* Rule Enabled Checkbox */}
                {(canEnable || canDisable) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!isDisabled}
                        onChange={() => (isDisabled ? handleEnable() : handleDisable())}
                        disabled={isEnabling || isDisabling}
                      />
                    }
                    label="Rule enabled"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
